// Client for the public Template Store + the super-admin management API.
//
// Three auth contexts:
//   • Public reads (browse store)  → no token, plain fetch.
//   • Install                      → user bearer token, via apiClient.request.
//   • Admin (/admin/*)             → admin token (from adminLogin), kept in
//                                    localStorage and sent as x-admin-token.
const { request, getApiBasePath } = require('./apiClient');

var ADMIN_TOKEN_KEY = 'alleflow_admin_token';

function getAdminToken() {
  try { return localStorage.getItem(ADMIN_TOKEN_KEY) || ''; } catch (e) { return ''; }
}

function setAdminToken(token) {
  try {
    if (token) localStorage.setItem(ADMIN_TOKEN_KEY, token);
    else localStorage.removeItem(ADMIN_TOKEN_KEY);
  } catch (e) { /* ignore */ }
}

function isAdminAuthed() {
  return !!getAdminToken();
}

// Plain fetch + JSON-or-throw, shared by the public and admin helpers.
function fetchJson(path, options) {
  var opts = options || {};
  return fetch(getApiBasePath() + path, {
    method: opts.method || 'GET',
    headers: opts.headers || { 'Content-Type': 'application/json' },
    body: opts.body ? JSON.stringify(opts.body) : undefined
  }).then(function(response) {
    return response.text().then(function(text) {
      var parsed = {};
      try { parsed = text ? JSON.parse(text) : {}; } catch (e) { parsed = {}; }
      if (!response.ok) {
        var err = new Error((parsed && parsed.error) || ('Request failed (' + response.status + ')'));
        err.status = response.status;
        throw err;
      }
      return parsed;
    });
  });
}

function adminHeaders() {
  return { 'Content-Type': 'application/json', 'x-admin-token': getAdminToken() };
}

// ── Public store ────────────────────────────────────────────────────────────

function listStoreTemplates() {
  return fetchJson('/public/templates').then(function(r) { return (r && r.data) || []; });
}

function getStoreTemplate(id) {
  return fetchJson('/public/templates/' + encodeURIComponent(id)).then(function(r) { return r && r.data; });
}

// ── Install (user) ──────────────────────────────────────────────────────────

function installTemplate(id) {
  return request('/templates/' + encodeURIComponent(id) + '/install', { method: 'POST' })
    .then(function(r) { return r && r.data; });
}

// Publish one of the signed-in user's own workflows to the store. No admin
// token — any authenticated user can publish.
function publishTemplate(payload) {
  return request('/templates/publish', { method: 'POST', body: payload || {} })
    .then(function(r) { return r && r.data; });
}

// Find the user's existing store listing for a workflow, if any. Lets the
// publish modal show "you're submitting v{n}" + a changelog field on re-publish.
// Resolves to null when the workflow hasn't been published before.
function getTemplateForWorkflow(workflowId) {
  return request('/templates/for-workflow/' + encodeURIComponent(workflowId))
    .then(function(r) { return r && r.data; })
    .catch(function() { return null; });
}

// Ask the AI (Haiku) to inspect the workflow and suggest the listing fields.
function aiFillTemplate(sourceWorkflowId) {
  return request('/templates/ai-fill', { method: 'POST', body: { sourceWorkflowId: sourceWorkflowId } })
    .then(function(r) { return r && r.data; });
}

// ── Admin ───────────────────────────────────────────────────────────────────

function adminLogin(username, password) {
  return fetchJson('/admin/login', { method: 'POST', body: { username: username, password: password } })
    .then(function(r) {
      if (r && r.token) setAdminToken(r.token);
      return r;
    });
}

function adminLogout() {
  setAdminToken('');
}

function adminListTemplates() {
  return fetchJson('/admin/templates', { headers: adminHeaders() }).then(function(r) { return (r && r.data) || []; });
}

function adminCreateTemplate(payload) {
  return fetchJson('/admin/templates', { method: 'POST', headers: adminHeaders(), body: payload })
    .then(function(r) { return r && r.data; });
}

function adminReviewTemplate(id, action) {
  return fetchJson('/admin/templates/' + encodeURIComponent(id) + '/review', { method: 'POST', headers: adminHeaders(), body: { action: action } })
    .then(function(r) { return r && r.data; });
}

function adminUpdateTemplate(id, payload) {
  return fetchJson('/admin/templates/' + encodeURIComponent(id), { method: 'PUT', headers: adminHeaders(), body: payload })
    .then(function(r) { return r && r.data; });
}

function adminDeleteTemplate(id) {
  return fetchJson('/admin/templates/' + encodeURIComponent(id), { method: 'DELETE', headers: adminHeaders() });
}

function adminListSourceWorkflows() {
  return fetchJson('/admin/source-workflows', { headers: adminHeaders() }).then(function(r) { return (r && r.data) || []; });
}

// ── Admin: beta waitlist ──────────────────────────────────────────────────────

function adminListWaitlist() {
  return fetchJson('/admin/waitlist', { headers: adminHeaders() }).then(function(r) { return (r && r.data) || []; });
}

function adminApproveWaitlist(id) {
  return fetchJson('/admin/waitlist/' + encodeURIComponent(id) + '/approve', { method: 'POST', headers: adminHeaders() })
    .then(function(r) { return r && r.entry; });
}

function adminRejectWaitlist(id) {
  return fetchJson('/admin/waitlist/' + encodeURIComponent(id) + '/reject', { method: 'POST', headers: adminHeaders() })
    .then(function(r) { return r && r.entry; });
}

// ── Admin: users + "Login as" ─────────────────────────────────────────────────

function adminListUsers() {
  return fetchJson('/admin/users', { headers: adminHeaders() }).then(function(r) { return (r && r.data) || []; });
}

// Resolves to { token, username } — a regular user session token the caller
// installs via apiClient.setAccessToken to browse the app as that user.
function adminLoginAsUser(id) {
  return fetchJson('/admin/users/' + encodeURIComponent(id) + '/login-as', { method: 'POST', headers: adminHeaders() });
}

module.exports = {
  // Exported so sibling admin stores (e.g. benchmarkStore) reuse the same
  // fetch/auth plumbing instead of copying it.
  fetchJson: fetchJson,
  adminHeaders: adminHeaders,
  getAdminToken: getAdminToken,
  setAdminToken: setAdminToken,
  isAdminAuthed: isAdminAuthed,
  listStoreTemplates: listStoreTemplates,
  getStoreTemplate: getStoreTemplate,
  installTemplate: installTemplate,
  publishTemplate: publishTemplate,
  getTemplateForWorkflow: getTemplateForWorkflow,
  aiFillTemplate: aiFillTemplate,
  adminLogin: adminLogin,
  adminLogout: adminLogout,
  adminListTemplates: adminListTemplates,
  adminCreateTemplate: adminCreateTemplate,
  adminReviewTemplate: adminReviewTemplate,
  adminUpdateTemplate: adminUpdateTemplate,
  adminDeleteTemplate: adminDeleteTemplate,
  adminListSourceWorkflows: adminListSourceWorkflows,
  adminListWaitlist: adminListWaitlist,
  adminApproveWaitlist: adminApproveWaitlist,
  adminRejectWaitlist: adminRejectWaitlist,
  adminListUsers: adminListUsers,
  adminLoginAsUser: adminLoginAsUser
};
